import type {
  DetectOverflowOptions,
  Middleware,
  SizeOptions,
} from '@floating-ui/react-dom';
import { detectOverflow, size } from '@floating-ui/react-dom';
import type { ImgHTMLAttributes } from 'react';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import * as styles from './contentPreviewPopOverSize.module.scss';

const PAPER_RATIO = 8.5 / 11;
const MAX_TEXTUAL_CONTENT_WIDTH = parseInt(styles.maxTextualContentWidth, 10);
const MAX_TEXTUAL_CONTENT_HEIGHT = MAX_TEXTUAL_CONTENT_WIDTH / PAPER_RATIO;

const ATTRIBUTE_NAME = 'data-floating-image';

interface FloatingContentImageProps
  extends ImgHTMLAttributes<HTMLImageElement> {
  onLoad: () => void;
}

export const FloatingContentImage = ({
  onLoad,
  ...props
}: FloatingContentImageProps) => {
  const ref = useRef<HTMLImageElement | null>(null);
  const onLoadRef = useRef(onLoad);

  useLayoutEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  useEffect(() => {
    if (!ref.current) return;

    ref.current.decode().then(() => {
      onLoad();
    });
  }, [onLoad]);

  return <img ref={ref} alt="" {...props} {...{ [ATTRIBUTE_NAME]: true }} />;
};

const getContentImageConstrain = (floating: HTMLElement) => {
  const element = floating.querySelector(`img[${ATTRIBUTE_NAME}]`);
  if (element instanceof HTMLImageElement && element.complete) {
    const rect = element.getBoundingClientRect();

    return {
      imageNaturalWidth: element.naturalWidth ?? 300,
      imageAspectRatio: rect.width ? rect.height / rect.width : null,
    };
  }
  return {};
};

const contentPreviewPopOverSize = (
  options: Partial<DetectOverflowOptions> & {
    fullWidth?: boolean;
    naturalWidth?: number;
  } = {},
): Middleware => ({
  name: 'contentPreviewPopOverSize',
  options,
  fn(state) {
    const apply: SizeOptions['apply'] = async ({
      availableHeight,
      availableWidth,
    }) => {
      const { imageNaturalWidth, imageAspectRatio } = getContentImageConstrain(
        state.elements.floating,
      );

      let width = Math.min(
        imageNaturalWidth ?? options.naturalWidth ?? MAX_TEXTUAL_CONTENT_WIDTH,
        availableWidth,
      );

      const leftPadding =
        typeof options.padding === 'number'
          ? options.padding
          : (options.padding?.left ?? 0);
      const rightPadding =
        typeof options.padding === 'number'
          ? options.padding
          : (options.padding?.right ?? 0);

      // eslint-disable-next-line no-param-reassign
      state.elements.floating.style.width = options.fullWidth
        ? `calc(100% - ${leftPadding}px - ${rightPadding}px)`
        : `${Math.floor(width)}px`;

      if (typeof imageAspectRatio === 'number') {
        const overflow = await detectOverflow(state, options);

        const boundaryHeight =
          state.rects.floating.height - overflow.top - overflow.bottom;
        const height = width * imageAspectRatio;
        if (height > boundaryHeight) {
          width = boundaryHeight / imageAspectRatio;
        }

        // eslint-disable-next-line no-param-reassign
        state.elements.floating.style.maxHeight = 'none';
      } else {
        // eslint-disable-next-line no-param-reassign
        state.elements.floating.style.maxHeight = `${Math.floor(
          Math.min(
            MAX_TEXTUAL_CONTENT_HEIGHT,
            width / PAPER_RATIO,
            availableHeight,
          ),
        )}px`;
      }
    };

    size({ ...options, apply }).fn(state);
    return {};
  },
});

export default contentPreviewPopOverSize;
